import _inject from "@stylexjs/stylex/lib/stylex-inject";
var _inject2 = _inject;
import * as stylex from "@stylexjs/stylex";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CalendarOff, ChevronRight, Swords } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { GUILD_RANK_CATALOG, GUILD_SPECIAL_TITLE_CATALOG } from "@/core/guild-members/titles";
import { absenceTrackingApi } from "@/lib/absence-tracking-api";
import { requireAdminBeforeLoad, requireAuthBeforeLoad } from "@/lib/auth-guard";
import { formatGuildJoinDate, guildJoinTenureDays } from "@/lib/guild-join-date";
import { guildWarApi, type GuildWarMemberDashboard } from "@/lib/guild-war-api";
import { memberDisplayLabel } from "@/lib/member-display-label";
import { profileApi } from "@/lib/profile-api";
import { OWN_PROFILE_ADMIN_ONLY } from "@/lib/profile-rollout";
import { guildWarKeys } from "@/lib/query-keys";
const absenceDashboardQueryKey = ["absence-tracking", "me"] as const;
_inject2({
  ltr: ".xh8yej3{width:100%}",
  priority: 4000
});
_inject2({
  ltr: ".x1x1rfll{max-width:none}",
  priority: 4000
});
_inject2({
  ltr: ".xvueqy4{margin-inline:auto}",
  priority: 2000
});
_inject2({
  ltr: ".xrvj5dj{display:grid}",
  priority: 3000
});
_inject2({
  ltr: ".x8fetqu{gap:1rem}",
  priority: 2000
});
_inject2({
  ltr: "@media (min-width: 768px){.x1iths6v.x1iths6v{gap:1.25rem}}",
  priority: 2200
});
_inject2({
  ltr: ".x107f5on{border-color:color-mix(in oklab,var(--primary) 10%,transparent)}",
  priority: 2000
});
_inject2({
  ltr: ".x78zum5{display:flex}",
  priority: 3000
});
_inject2({
  ltr: ".xdt5ytf{flex-direction:column}",
  priority: 3000
});
_inject2({
  ltr: "@media (min-width: 640px){.x2kc0e2.x2kc0e2{flex-direction:row}}",
  priority: 3200
});
_inject2({
  ltr: "@media (min-width: 640px){.x10isrdx.x10isrdx{align-items:flex-start}}",
  priority: 3200
});
_inject2({
  ltr: "@media (min-width: 640px){.xbbtznn.xbbtznn{gap:1.5rem}}",
  priority: 2200
});
_inject2({
  ltr: ".xdvn7xf{width:6rem}",
  priority: 4000
});
_inject2({
  ltr: ".x17frcva{height:6rem}",
  priority: 4000
});
_inject2({
  ltr: ".xgj90kv{background-color:color-mix(in oklab,var(--primary) 10%,transparent)}",
  priority: 3000
});
_inject2({
  ltr: ".xzwifym{color:var(--primary)}",
  priority: 3000
});
_inject2({
  ltr: "@media (min-width: 640px){.x52o94w.x52o94w{width:8rem}}",
  priority: 4200
});
_inject2({
  ltr: "@media (min-width: 640px){.x5nokh7.x5nokh7{height:8rem}}",
  priority: 4200
});
_inject2({
  ltr: ".xcg35fi{font-size:1.875rem}",
  priority: 3000
});
_inject2({
  ltr: ".x16n75kc{line-height:2.25rem}",
  priority: 3000
});
_inject2({
  ltr: ".x1s688f{font-weight:600}",
  priority: 3000
});
_inject2({
  ltr: ".xeuugli{min-width:0}",
  priority: 4000
});
_inject2({
  ltr: ".x8233eu{gap:.75rem}",
  priority: 2000
});
_inject2({
  ltr: ".xboafo0{font-size:.75rem}",
  priority: 3000
});
_inject2({
  ltr: ".xl2ypbo{line-height:1rem}",
  priority: 3000
});
_inject2({
  ltr: ".xrvchh{color:var(--muted-foreground)}",
  priority: 3000
});
_inject2({
  ltr: ".x1xlr1w8{font-weight:700}",
  priority: 3000
});
_inject2({
  ltr: ".x11fj6oh{letter-spacing:.18em}",
  priority: 3000
});
_inject2({
  ltr: ".xtvhhri{text-transform:uppercase}",
  priority: 3000
});
_inject2({
  ltr: ".x1tbvfm1{margin-top:.25rem}",
  priority: 4000
});
_inject2({
  ltr: ".x1mzt3pk{overflow-wrap:break-word}",
  priority: 3000
});
_inject2({
  ltr: ".x1w69ole{letter-spacing:-0.025em}",
  priority: 3000
});
_inject2({
  ltr: "@media (min-width: 640px){.xs7o2sf.xs7o2sf{font-size:2.25rem}}",
  priority: 3200
});
_inject2({
  ltr: "@media (min-width: 640px){.x17z2gyq.x17z2gyq{line-height:2.5rem}}",
  priority: 3200
});
_inject2({
  ltr: ".xvh977a{gap:.25rem}",
  priority: 2000
});
_inject2({
  ltr: ".xkpwil5{font-size:.875rem}",
  priority: 3000
});
_inject2({
  ltr: ".x8r4c90{line-height:1.25rem}",
  priority: 3000
});
_inject2({
  ltr: ".x13z6uf9{gap:.5rem}",
  priority: 2000
});
_inject2({
  ltr: ".x1a02dak{flex-wrap:wrap}",
  priority: 3000
});
_inject2({
  ltr: ".x10hpsqq{border-radius:9999px}",
  priority: 2000
});
_inject2({
  ltr: ".x1ryrjj2{padding-inline:.75rem}",
  priority: 2000
});
_inject2({
  ltr: ".x1o5vn8x{padding-block:.25rem}",
  priority: 2000
});
_inject2({
  ltr: ".xk50ysn{font-weight:500}",
  priority: 3000
});
_inject2({
  ltr: ".xuhz2es{background-color:var(--muted)}",
  priority: 3000
});
_inject2({
  ltr: ".xo1l8bm{font-weight:400}",
  priority: 3000
});
_inject2({
  ltr: ".x11jfisy{color:var(--foreground)}",
  priority: 3000
});
_inject2({
  ltr: "@media (min-width: 768px){.x1um9e8z.x1um9e8z{grid-template-columns:repeat(2,minmax(0,1fr))}}",
  priority: 3200
});
_inject2({
  ltr: ".x1lliihq{display:block}",
  priority: 3000
});
_inject2({
  ltr: ".x1j6x5gx{border-radius:1.5rem}",
  priority: 2000
});
_inject2({
  ltr: ".x1a2a7pz{outline:none}",
  priority: 2000
});
_inject2({
  ltr: ".x1v7yk1c:focus-visible{box-shadow:0 0 0 3px color-mix(in oklab,var(--ring) 50%,transparent)}",
  priority: 3040
});
_inject2({
  ltr: ".x5yr21d{height:100%}",
  priority: 4000
});
_inject2({
  ltr: ".x6s0dn4{align-items:center}",
  priority: 3000
});
_inject2({
  ltr: ".xwqq7k2{width:1.25rem}",
  priority: 4000
});
_inject2({
  ltr: ".xmll18r{height:1.25rem}",
  priority: 4000
});
_inject2({
  ltr: ".x2lah0s{flex-shrink:0}",
  priority: 3000
});
_inject2({
  ltr: ".x1iyjqo2{flex-grow:1}",
  priority: 3000
});
_inject2({
  ltr: ".x1pha0wt{align-items:baseline}",
  priority: 3000
});
_inject2({
  ltr: ".x4prdry{column-gap:.5rem}",
  priority: 3000
});
_inject2({
  ltr: ".x4arjd5{row-gap:.125rem}",
  priority: 3000
});
_inject2({
  ltr: "@media (min-width: 1280px){.x6k0n6g.x6k0n6g{flex-wrap:nowrap}}",
  priority: 3200
});
_inject2({
  ltr: "@media (min-width: 1280px){.x1ndb9ql.x1ndb9ql{white-space:nowrap}}",
  priority: 3200
});
_inject2({
  ltr: ".xcdlrvm{width:1rem}",
  priority: 4000
});
_inject2({
  ltr: ".x1l36t39{height:1rem}",
  priority: 4000
});
const styles = {
  profileCard: {
    kVAM5u: "x107f5on",
    $$css: true
  },
  profileContent: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x8fetqu",
    kXbiDp: "x2kc0e2",
    kTGtLM: "x10isrdx",
    k2vOR9: "xbbtznn",
    $$css: true
  },
  avatar: {
    kzqmXN: "xdvn7xf",
    kZKoxP: "x17frcva",
    kWkggS: "xgj90kv",
    kMwMTN: "xzwifym",
    k3ZSdY: "x52o94w",
    kOf9i4: "x5nokh7",
    $$css: true
  },
  avatarFallback: {
    kGuDYH: "xcg35fi",
    kLWn49: "x16n75kc",
    k63SB2: "x1s688f",
    $$css: true
  },
  fillCard: {
    kZKoxP: "x5yr21d",
    $$css: true
  },
  statusContent: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x8233eu",
    $$css: true
  }
};
export const Route = createFileRoute("/_authed/profile")({
  beforeLoad: ({
    context,
    location
  }) => (OWN_PROFILE_ADMIN_ONLY ? requireAdminBeforeLoad : requireAuthBeforeLoad)({
    context,
    location
  }),
  component: ProfilePage
});
function getGuildWarStatus(dashboard: GuildWarMemberDashboard): string {
  if (dashboard.weeklySignup?.state === "active") {
    if (dashboard.weeklySignup.availability === "saturday") return "Signed up for Saturday";
    if (dashboard.weeklySignup.availability === "sunday") return "Signed up for Sunday";
    return "Signed up for both days";
  }
  if (dashboard.isPermanentMember) return "Permanent member";
  if (dashboard.pendingPermanentApplication?.state === "pending") return "Permanent application pending";
  if (dashboard.week?.state === "signup_open") return "Not signed up";
  if (dashboard.week) return "Guild War signup closed";
  return "No active or upcoming Guild War week";
}
function ProfilePage() {
  const {
    auth
  } = Route.useRouteContext();
  const profileQuery = useQuery({
    queryKey: ["profile", auth.user?.userId] as const,
    queryFn: profileApi.get
  });
  const guildWarQuery = useQuery({
    queryKey: [...guildWarKeys.meDashboard(), auth.user?.userId] as const,
    queryFn: guildWarApi.getMyDashboard
  });
  const absenceQuery = useQuery({
    queryKey: [...absenceDashboardQueryKey, auth.user?.userId] as const,
    queryFn: absenceTrackingApi.getMyDashboard
  });
  if (!auth.user) return null;
  const authDisplayName = auth.user.displayName?.trim();
  const safeAuthDisplayName = authDisplayName && authDisplayName.toLowerCase() !== auth.user.username?.trim().toLowerCase() ? authDisplayName : null;
  const label = memberDisplayLabel({
    displayName: profileQuery.data?.displayName ?? safeAuthDisplayName,
    memberId: auth.user.userId
  });
  const initials = label.split(/\s+/).filter(part => part.length > 0).slice(0, 2).map(part => part[0]?.toUpperCase() ?? "").join("");
  const rankLabel = GUILD_RANK_CATALOG.find(({
    value
  }) => value === profileQuery.data?.rankTitle)?.label;
  const specialLabels = GUILD_SPECIAL_TITLE_CATALOG.filter(({
    value
  }) => profileQuery.data?.specialTitles.includes(value)).map(({
    label
  }) => label);
  const joinedAt = profileQuery.data?.joinedAt ? new Date(profileQuery.data.joinedAt) : null;
  const hasValidJoinDate = joinedAt && !Number.isNaN(joinedAt.getTime());
  const tenure = hasValidJoinDate ? `Member for ${guildJoinTenureDays(joinedAt)} days` : "Membership tenure unavailable";
  const joinDate = hasValidJoinDate ? `Joined ${formatGuildJoinDate(joinedAt)}` : "Join date unavailable";
  const guildWarStatus = guildWarQuery.isLoading ? "Loading Guild War status..." : guildWarQuery.isError || !guildWarQuery.data ? "Guild War status unavailable" : getGuildWarStatus(guildWarQuery.data);
  const absenceStatus = absenceQuery.isLoading ? "Loading absence status..." : absenceQuery.isError || !absenceQuery.data ? "Absence status unavailable" : absenceQuery.data.activeItems.length === 0 ? "No active or upcoming absence" : absenceQuery.data.activeItems.length === 1 ? "1 active or upcoming absence" : `${absenceQuery.data.activeItems.length} active or upcoming absences`;
  return <div className="xh8yej3 x1x1rfll xvueqy4 xrvj5dj x8fetqu x1iths6v">
      <Card size="lg" stylexStyle={styles.profileCard}>
        <CardContent stylexStyle={styles.profileContent}>
          <Avatar stylexStyle={styles.avatar}>
            {profileQuery.data?.avatarUrl ? <AvatarImage src={profileQuery.data.avatarUrl} alt={label} /> : null}
            <AvatarFallback stylexStyle={styles.avatarFallback}>
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="xeuugli xrvj5dj x8233eu">
            <div>
              <p className="xboafo0 xl2ypbo xrvchh x1xlr1w8 x11fj6oh xtvhhri">Profile</p>
              <h1 className="x1tbvfm1 x1mzt3pk xcg35fi x16n75kc x1s688f x1w69ole xs7o2sf x17z2gyq">{label}</h1>
            </div>
            <div className="xrvj5dj xvh977a xkpwil5 x8r4c90 xrvchh">
              <p>{tenure}</p>
              <p>{joinDate}</p>
            </div>
            <div className="xrvj5dj x13z6uf9">
              <p className="xboafo0 xl2ypbo xrvchh x1xlr1w8 x11fj6oh xtvhhri">Guild titles</p>
              {rankLabel || specialLabels.length > 0 ? <div className="x78zum5 x1a02dak x13z6uf9">
                  {rankLabel ? <span className="x10hpsqq xgj90kv x1ryrjj2 x1o5vn8x xkpwil5 x8r4c90 xk50ysn xzwifym">{rankLabel}</span> : null}
                  {specialLabels.map(title => <span key={title} className="x10hpsqq xuhz2es x1ryrjj2 x1o5vn8x xkpwil5 x8r4c90 xo1l8bm x11jfisy">
                      {title}
                    </span>)}
                </div> : <p className="xkpwil5 x8r4c90 xrvchh">
                  No guild titles assigned
                </p>}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="xrvj5dj x8fetqu x1um9e8z">
        <Link to="/guild-war-signup" className="x1lliihq x1j6x5gx x1a2a7pz x1v7yk1c">
          <Card variant="interactive" stylexStyle={styles.fillCard}>
            <CardContent stylexStyle={styles.statusContent}>
              <Swords className="xwqq7k2 xmll18r x2lah0s xzwifym" />
              <div className="x78zum5 xeuugli x1iyjqo2 x1a02dak x1pha0wt x4prdry x4arjd5 x6k0n6g">
                <h2 className="x2lah0s x1s688f">Guild War</h2>
                <p className="xkpwil5 x8r4c90 xrvchh x1ndb9ql">{guildWarStatus}</p>
              </div>
              <ChevronRight className="xcdlrvm x1l36t39 x2lah0s xrvchh" />
            </CardContent>
          </Card>
        </Link>

        <Link to="/absences" className="x1lliihq x1j6x5gx x1a2a7pz x1v7yk1c">
          <Card variant="interactive" stylexStyle={styles.fillCard}>
            <CardContent stylexStyle={styles.statusContent}>
              <CalendarOff className="xwqq7k2 xmll18r x2lah0s xzwifym" />
              <div className="x78zum5 xeuugli x1iyjqo2 x1a02dak x1pha0wt x4prdry x4arjd5 x6k0n6g">
                <h2 className="x2lah0s x1s688f">Absences</h2>
                <p className="xkpwil5 x8r4c90 xrvchh x1ndb9ql">{absenceStatus}</p>
              </div>
              <ChevronRight className="xcdlrvm x1l36t39 x2lah0s xrvchh" />
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>;
}